/**
 * System Monitoring Component
 *
 * Displays system health metrics, node status, GPU utilization,
 * and overall platform health.
 */

import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { getApiClient, isSuccess } from '../../api/client';

// =============================================================================
// Types
// =============================================================================

interface NodeMetrics {
  id: string;
  name: string;
  status: 'ONLINE' | 'OFFLINE' | 'MAINTENANCE' | 'ERROR';
  lastSeen: string | null;
  capabilities: {
    gpu: boolean;
    gpuType?: string;
    gpuVram?: number;
    cpu: number;
    memoryMB: number;
  };
  metrics?: {
    cpuUsage: number;
    memoryUsage: number;
    gpuUsage?: number;
    gpuMemoryUsage?: number;
  };
  containerCount: number;
}

interface BotMetrics {
  total: number;
  running: number;
  stopped: number;
  error: number;
}

interface SystemMetrics {
  nodes: NodeMetrics[];
  bots: BotMetrics;
  totalUsers: number;
  activeConnections: number;
}

interface GpuInfo {
  nodeId: string;
  nodeName: string;
  gpuType: string;
  vram: number;
  usage: number;
  memoryUsage: number;
  temperature?: number;
}

// =============================================================================
// Component
// =============================================================================

export function SystemMonitoring() {
  useAuth(); // Ensure user is authenticated
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [gpuMetrics, setGpuMetrics] = useState<GpuInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMetrics = useCallback(async () => {
    try {
      setRefreshing(true);
      const apiClient = getApiClient();

      const [dashboardRes, nodesRes, gpuRes] = await Promise.all([
        apiClient.get<{ summary: { totalBots: number; runningBots: number }; totalUsers?: number; activeConnections?: number }>('/monitoring/dashboard'),
        apiClient.get<{ nodes: NodeMetrics[] }>('/monitoring/nodes'),
        apiClient.get<GpuInfo[]>('/monitoring/gpus').catch(() => ({ success: true as const, data: [] })),
      ]);

      if (!isSuccess(dashboardRes) || !isSuccess(nodesRes)) {
        throw new Error('Failed to fetch monitoring data');
      }

      const dashboardData = dashboardRes.data;
      const nodesData = nodesRes.data;
      const gpuData = isSuccess(gpuRes) ? gpuRes.data : [];

      setMetrics({
        nodes: nodesData.nodes || [],
        bots: {
          total: dashboardData.summary?.totalBots || 0,
          running: dashboardData.summary?.runningBots || 0,
          stopped: 0,
          error: 0
        },
        totalUsers: dashboardData.totalUsers || 0,
        activeConnections: dashboardData.activeConnections || 0,
      });
      setGpuMetrics(gpuData);
      setError(null);
    } catch (err) {
      setError('Failed to load monitoring data');
      console.error('Monitoring fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ONLINE':
        return 'bg-green-500';
      case 'OFFLINE':
        return 'bg-gray-500';
      case 'MAINTENANCE':
        return 'bg-yellow-500';
      case 'ERROR':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getUsageColor = (usage: number) => {
    if (usage >= 90) return 'bg-red-500';
    if (usage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-discord-blurple border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">System Monitoring</h1>
            <p className="mt-1 text-sm text-gray-400">
              Real-time system health and resource utilization
            </p>
          </div>
          <button
            onClick={fetchMetrics}
            disabled={refreshing}
            className="flex items-center gap-2 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/80 disabled:opacity-50"
          >
            {refreshing ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Refresh
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-red-500/10 border border-red-500/20 p-4 text-red-400">
            {error}
          </div>
        )}

        {/* Overview Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Total Bots</div>
            <div className="mt-1 text-2xl font-bold text-white">{metrics?.bots.total || 0}</div>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-green-400">{metrics?.bots.running || 0} running</span>
              <span className="text-gray-400">{metrics?.bots.stopped || 0} stopped</span>
              {(metrics?.bots.error || 0) > 0 && (
                <span className="text-red-400">{metrics?.bots.error} error</span>
              )}
            </div>
          </div>

          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Compute Nodes</div>
            <div className="mt-1 text-2xl font-bold text-white">{metrics?.nodes.length || 0}</div>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-green-400">
                {metrics?.nodes.filter(n => n.status === 'ONLINE').length || 0} online
              </span>
              <span className="text-gray-400">
                {metrics?.nodes.filter(n => n.status === 'OFFLINE').length || 0} offline
              </span>
            </div>
          </div>

          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Active GPUs</div>
            <div className="mt-1 text-2xl font-bold text-white">{gpuMetrics.length}</div>
            <div className="mt-2 text-xs text-gray-400">
              {gpuMetrics.length > 0
                ? `Avg load: ${Math.round(gpuMetrics.reduce((a, g) => a + g.usage, 0) / gpuMetrics.length)}%`
                : 'No GPUs detected'}
            </div>
          </div>

          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Total Users</div>
            <div className="mt-1 text-2xl font-bold text-white">{metrics?.totalUsers || 0}</div>
            <div className="mt-2 text-xs text-gray-400">
              {metrics?.activeConnections || 0} active connections
            </div>
          </div>
        </div>

        {/* Node Status */}
        <div className="rounded-lg bg-discord-dark p-6">
          <h2 className="mb-4 text-lg font-semibold text-white">Compute Nodes</h2>
          {metrics?.nodes && metrics.nodes.length > 0 ? (
            <div className="space-y-4">
              {metrics.nodes.map((node) => (
                <div
                  key={node.id}
                  className="rounded-lg border border-discord-light bg-discord-darker p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-3 w-3 rounded-full ${getStatusColor(node.status)}`} />
                      <div>
                        <div className="font-medium text-white">{node.name}</div>
                        <div className="text-sm text-gray-400">
                          {node.capabilities.gpu
                            ? `${node.capabilities.gpuType} (${node.capabilities.gpuVram}GB VRAM)`
                            : 'CPU only'}
                          {' | '}
                          {node.capabilities.cpu} cores, {Math.round(node.capabilities.memoryMB / 1024)}GB RAM
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-400">
                        {node.containerCount} container{node.containerCount !== 1 ? 's' : ''}
                      </div>
                      {node.lastSeen && (
                        <div className="text-xs text-gray-500">
                          Last seen: {new Date(node.lastSeen).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  {node.metrics && (
                    <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      <div>
                        <div className="mb-1 flex justify-between text-xs">
                          <span className="text-gray-400">CPU</span>
                          <span className="text-white">{node.metrics.cpuUsage}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-discord-light">
                          <div
                            className={`h-2 rounded-full ${getUsageColor(node.metrics.cpuUsage)}`}
                            style={{ width: `${node.metrics.cpuUsage}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 flex justify-between text-xs">
                          <span className="text-gray-400">Memory</span>
                          <span className="text-white">{node.metrics.memoryUsage}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-discord-light">
                          <div
                            className={`h-2 rounded-full ${getUsageColor(node.metrics.memoryUsage)}`}
                            style={{ width: `${node.metrics.memoryUsage}%` }}
                          />
                        </div>
                      </div>
                      {node.metrics.gpuUsage !== undefined && (
                        <>
                          <div>
                            <div className="mb-1 flex justify-between text-xs">
                              <span className="text-gray-400">GPU</span>
                              <span className="text-white">{node.metrics.gpuUsage}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-discord-light">
                              <div
                                className={`h-2 rounded-full ${getUsageColor(node.metrics.gpuUsage)}`}
                                style={{ width: `${node.metrics.gpuUsage}%` }}
                              />
                            </div>
                          </div>
                          <div>
                            <div className="mb-1 flex justify-between text-xs">
                              <span className="text-gray-400">VRAM</span>
                              <span className="text-white">{node.metrics.gpuMemoryUsage}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-discord-light">
                              <div
                                className={`h-2 rounded-full ${getUsageColor(node.metrics.gpuMemoryUsage || 0)}`}
                                style={{ width: `${node.metrics.gpuMemoryUsage}%` }}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              No compute nodes registered
            </div>
          )}
        </div>

        {/* GPU Details */}
        {gpuMetrics.length > 0 && (
          <div className="rounded-lg bg-discord-dark p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">GPU Resources</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-discord-light text-left text-sm text-gray-400">
                    <th className="pb-3 pr-4">Node</th>
                    <th className="pb-3 pr-4">GPU</th>
                    <th className="pb-3 pr-4">VRAM</th>
                    <th className="pb-3 pr-4">Usage</th>
                    <th className="pb-3 pr-4">Memory</th>
                    <th className="pb-3">Temp</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {gpuMetrics.map((gpu, idx) => (
                    <tr key={idx} className="border-b border-discord-light/50">
                      <td className="py-3 pr-4 text-white">{gpu.nodeName}</td>
                      <td className="py-3 pr-4 text-gray-300">{gpu.gpuType}</td>
                      <td className="py-3 pr-4 text-gray-300">{gpu.vram}GB</td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-20 rounded-full bg-discord-light">
                            <div
                              className={`h-2 rounded-full ${getUsageColor(gpu.usage)}`}
                              style={{ width: `${gpu.usage}%` }}
                            />
                          </div>
                          <span className="text-white">{gpu.usage}%</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-20 rounded-full bg-discord-light">
                            <div
                              className={`h-2 rounded-full ${getUsageColor(gpu.memoryUsage)}`}
                              style={{ width: `${gpu.memoryUsage}%` }}
                            />
                          </div>
                          <span className="text-white">{gpu.memoryUsage}%</span>
                        </div>
                      </td>
                      <td className="py-3 text-gray-300">
                        {gpu.temperature ? `${gpu.temperature}°C` : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default SystemMonitoring;
